import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH, VDJ_PRESETS } from './lib/vdjLook.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { SETS, CAM_D, BG_Z, placeObject, coverFrame } from './lib/glass-sets.js';

// Contrat du rig partagé : interdit. Le verre n'a que le fond pour lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'glass',
  family: 'geometric',
  danceability: 0.66,
  energyLevel: 0.52,
  bpmMin: 85,
  bpmMax: 130,
  movement: 0.45,
  tonality: 0.70,
  enabled: true
};

// GLASS : des objets de verre massif (prismes, sphère, cube, cristal taillé,
// lentille) qui flottent et tournent lentement devant la pochette agrandie ;
// à travers eux, la pochette est RÉFRACTÉE, inversée, démultipliée, avec la
// dispersion chromatique sur les arêtes. House mélodique, pop, électro,
// downtempo, R&B. Règles R1 à R19, preset lounge de vdjLook (party au-dessus
// de 0,7 d'intensité, adapt.js), horloge de scène partagée (« R20 »).
//
// LE FOND : une cible privée pleine résolution ; la pochette en mode COUVRIR
// (R18 : échelle = max des deux rapports, dérive Ken Burns comprise dans la
// marge, ancrée sur le centre de composition), nette, assombrie ; variantes par
// phrase : la grille 3 x 3, les bandes de la palette. Des éclats (caustiques
// simples) y passent quand un prisme tourne.
//
// LE VERRE, le détail décisif : réfraction en espace écran en DEUX PASSES.
// 1. Les faces ARRIÈRE de tous les objets écrivent leur normale et leur
//    profondeur dans une cible (demi-résolution aux tiers 0 et 1).
// 2. Les faces AVANT : la vue est réfractée à l'entrée (normale avant, indice
//    1,5) puis à la sortie (normale arrière lue dans la cible, réflexion
//    totale si besoin) ; l'épaisseur = profondeur arrière - avant déplace la
//    lecture du fond. Trois lectures à des indices légèrement différents (R, V,
//    B) : la DISPERSION fait des arcs-en-ciel sur les arêtes, nets parce que
//    le fond est net et lu en LinearFilter. Réflexion de Fresnel (Schlick,
//    F0 = 0,04) d'un environnement simple (gradient, bande claire), arêtes
//    éclaircies par le Fresnel rasant. Objets à facettes en normales plates
//    (chaque facette réfracte autrement : le verre taillé), sphère et lentille
//    lisses. Cible MSAA privée pour des contours nets.
//
// LA MUSIQUE : rotation lente, respiration de taille sur le temps (lounge :
// 6 %, party : 12 %), éclat de Fresnel sur une arête à chaque temps (un objet
// à la fois), à-coup de rotation sur la mesure, bands.low resserre les objets
// vers le centre et les grossit, bands.high augmente la dispersion ; PHRASE :
// variante du fond ; SCÈNE : ensemble d'objets suivant (fondu de taille) ;
// DROP (adapt.dropReady) : les objets s'alignent devant le centre et le fond
// est plié en kaléidoscope à six secteurs pendant deux mesures, flash seulement
// au-dessus de 0,7 d'intensité.
//
// Debug : ?glassset=<0..5>, ?glassdisp=<f> (dispersion fixe),
// ?glassbg=cover|grid|bands, ?glassobjs=0 (fond seul), ?glassprobe=1.

const TIER = {
  0: { objs: 2, backScale: 0.5, msaa: 0, disp: 'single' },
  1: { objs: 5, backScale: 0.5, msaa: 4, disp: 'triple' },
  2: { objs: 5, backScale: 1.0, msaa: 4, disp: 'triple' }
};
const BG_DIM = 0.55;
const SCENE_S = 22;
const BG_MODES = ['cover', 'grid', 'bands', 'grey'];     // grey : sonde seulement (mesure de la dispersion), jamais dans le cycle
const BG_CYCLE = 3;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

// ------------------------------------------------------------------ GLSL
const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const BG_FRAGMENT = `
  precision highp float;
  uniform sampler2D uCover;
  uniform float uHasCover, uAspect, uMode, uDim, uTime;
  uniform vec4 uFrame;              // visW, visH, cx, cy (R18)
  uniform vec2 uCenter;             // le centre de composition (unités de hauteur)
  uniform vec3 uColA, uColB, uColC;
  uniform vec4 uCaustic[5];         // x, y (écran), angle, intensité
  uniform float uKaleido;
  varying vec2 vUv;
  vec2 kal(vec2 p) {
    float a = atan(p.y, p.x), r = length(p), w = 6.2831853 / 6.0;
    a = mod(a, w); if (a > w * 0.5) a = w - a;
    return vec2(cos(a), sin(a)) * r;
  }
  vec3 cover(vec2 s) {
    // s : écran en unités de hauteur, centré sur le centre de composition
    vec2 q = s / vec2(uAspect, 1.0) * 0.5 + 0.5;                 // [0, 1]
    vec2 uv = uFrame.zw + (q - 0.5) * uFrame.xy;
    return uHasCover > 0.5 ? texture2D(uCover, uv).rgb : mix(uColA, uColB, q.y) * 0.5;
  }
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 sc = s - uCenter;
    if (uKaleido > 0.0) sc = mix(sc, kal(sc), uKaleido);
    vec3 col;
    if (uMode < 0.5) col = cover(sc);
    else if (uMode < 1.5) {
      // la grille 3 x 3 : chaque case montre la pochette entière (couverte dans sa case), les cases alternent leur miroir
      vec2 g = (sc / vec2(uAspect, 1.0) * 0.5 + 0.5) * 3.0;
      vec2 cell = floor(g), f = fract(g);
      if (mod(cell.x + cell.y, 2.0) > 0.5) f.x = 1.0 - f.x;
      vec2 uv = uFrame.zw + (f - 0.5) * uFrame.xy;
      col = uHasCover > 0.5 ? texture2D(uCover, uv).rgb : mix(uColA, uColB, f.y) * 0.5;
      col *= 0.92 + 0.08 * sin(uTime * 0.8 + cell.x * 2.1 + cell.y * 1.3);
    } else if (uMode > 2.5) {
      col = vec3(0.5);
    } else {
      // les bandes de la palette, obliques, qui glissent lentement
      float b = (sc.y * 0.7 + sc.x * 0.25) * 2.2 + uTime * 0.05;
      float k = floor(b), f = fract(b);
      vec3 c0 = mod(k, 3.0) < 0.5 ? uColA : (mod(k, 3.0) < 1.5 ? uColB : uColC);
      vec3 c1 = mod(k + 1.0, 3.0) < 0.5 ? uColA : (mod(k + 1.0, 3.0) < 1.5 ? uColB : uColC);
      col = mix(c0, c1, smoothstep(0.85, 1.0, f)) * 0.55;
    }
    col *= uDim;
    // les caustiques : un croissant de lumière fin (~4 px) et irisé, projeté à côté
    // de chaque objet qui tourne vite, du côté de sa rotation, qui s'éteint en douceur
    for (int i = 0; i < 5; i++) {
      vec4 c = uCaustic[i];
      if (c.w <= 0.001) continue;
      vec2 dir = vec2(cos(c.z), sin(c.z));
      vec2 p = s - c.xy - dir * 0.55;                        // le centre de l'arc, décalé du côté de la rotation
      float rr = length(p) - 0.42;                           // l'arc : rayon 0,42
      float facing = smoothstep(0.1, 0.6, dot(normalize(p + 1e-5), dir));
      float arc = exp(-rr * rr / (0.006 * 0.006 * 4.0)) * facing;
      float h = clamp(rr / 0.012 * 0.5 + 0.5, 0.0, 1.0);    // l'irisation à travers l'épaisseur de l'arc
      vec3 iris = 0.55 + 0.45 * cos(6.2831853 * (h + vec3(0.0, 0.33, 0.67)));
      col += iris * arc * c.w * 0.45;
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;
// 1. les faces arrière : normale (monde) et profondeur de vue
const BACK_VERTEX = `
  varying vec3 vN;
  varying float vZ;
  void main() {
    vN = normalize(mat3(modelMatrix) * normal);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vZ = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;
const BACK_FRAGMENT = `
  precision highp float;
  varying vec3 vN;
  varying float vZ;
  void main() { gl_FragColor = vec4(normalize(vN), vZ); }
`;
// 2. les faces avant : la réfraction, la dispersion, le Fresnel
const GLASS_VERTEX = `
  varying vec3 vN;
  varying vec3 vW;
  varying float vZ;
  void main() {
    vN = normalize(mat3(modelMatrix) * normal);
    vec4 w = modelMatrix * vec4(position, 1.0);
    vW = w.xyz;
    vec4 mv = viewMatrix * w;
    vZ = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;
function glassFragment(triple) {
  return `
  precision highp float;
  uniform sampler2D tBg, tBack;
  uniform vec2 uRes;
  uniform mat4 uProjView;
  uniform float uIor, uDisp, uBgZ, uSpark, uGain, uTime;
  uniform vec3 uColA, uColB;
  varying vec3 vN;
  varying vec3 vW;
  varying float vZ;
  vec3 env(vec3 d) {
    // un environnement simple et sombre : deux fenêtres claires (une haute, une
    // oblique), une lueur de palette au sol ; le verre ne s'y lit qu'au Fresnel
    float up = d.y * 0.5 + 0.5;
    vec3 c = mix(vec3(0.015, 0.015, 0.025), vec3(0.10, 0.11, 0.14), up);
    float w1 = exp(-pow((d.x * 0.6 + d.y - 0.45) * 5.0, 2.0));
    float w2 = exp(-pow((d.x * -0.8 + d.y * 0.3 + 0.2) * 7.0, 2.0)) * 0.5;
    return c + vec3(1.0) * (w1 + w2) * 0.8 + uColA * 0.06 * (1.0 - up);
  }
  // la lecture du fond pour un canal : réfraction à l'entrée (normale avant) puis à
  // la sortie (normale arrière lue dans la cible), le rayon sortant coupe le plan du
  // fond z = uBgZ ; le point est projeté avec la caméra : grossissement et
  // inversion exacts (une sphère inverse l'image, une lentille la grossit)
  vec2 bend(vec3 E, vec3 I, vec3 N, vec3 Nb, float eta, float thick, vec2 suv) {
    vec3 r1 = refract(I, N, eta);
    if (dot(r1, r1) < 1e-6) r1 = reflect(I, N);
    vec3 X = E + r1 * (thick / max(0.35, -r1.z));
    vec3 r2 = refract(r1, -Nb, 1.0 / eta);
    if (dot(r2, r2) < 1e-6) r2 = reflect(r1, -Nb);
    if (r2.z > -0.05) r2 = normalize(vec3(r2.xy, -0.05));   // un rayon qui ne redescend pas vers le fond : rasant
    vec3 P = X + r2 * ((uBgZ - X.z) / r2.z);
    vec4 clip = uProjView * vec4(P, 1.0);
    vec2 uv = clip.xy / clip.w * 0.5 + 0.5;
    return clamp(uv, 0.001, 0.999);
  }
  void main() {
    vec2 suv = gl_FragCoord.xy / uRes;
    vec3 N = normalize(vN);
    vec3 V = normalize(cameraPosition - vW);
    vec3 I = -V;
    vec4 back = texture2D(tBack, suv);
    vec3 Nb = back.a > 0.0 ? normalize(back.xyz) : -N;
    float thick = clamp(back.a - vZ, 0.02, 1.2);
    float cosT = max(dot(N, V), 0.0);
    float eta = 1.0 / uIor;
    ${triple ? `
    vec2 uR = bend(vW, I, N, Nb, 1.0 / (uIor - uDisp), thick, suv);
    vec2 uG = bend(vW, I, N, Nb, eta, thick, suv);
    vec2 uB = bend(vW, I, N, Nb, 1.0 / (uIor + uDisp), thick, suv);
    vec3 refr = vec3(texture2D(tBg, uR).r, texture2D(tBg, uG).g, texture2D(tBg, uB).b);`
    : `
    vec2 uG = bend(vW, I, N, Nb, eta, thick, suv);
    vec2 uB = bend(vW, I, N, Nb, 1.0 / (uIor + uDisp), thick, suv);
    vec3 c0 = texture2D(tBg, uG).rgb, c1 = texture2D(tBg, uB).rgb;
    vec3 refr = vec3(c0.r, mix(c0.g, c1.g, 0.5), c1.b);`}
    // le verre : une absorption légère, teintée
    refr *= vec3(0.96, 0.985, 1.0);
    float F = 0.04 + 0.96 * pow(1.0 - cosT, 5.0);
    vec3 R = reflect(I, N);
    vec3 col = mix(refr, env(R), F);
    // les arêtes un peu plus claires (Fresnel rasant), l'éclat du temps
    float rim = pow(1.0 - cosT, 4.0);
    col += (0.06 + uSpark * 0.7) * rim * mix(vec3(1.0), uColB, 0.3);
    gl_FragColor = vec4(col * uGain, 1.0);
  }
  `;
}
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;
const LUM_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { vec3 c = texture2D(uTex, vUv).rgb; gl_FragColor = vec4(vec3(pow(dot(c, vec3(0.2126, 0.7152, 0.0722)), 1.0 / 2.2)), 1.0); }
`;

// les maillages : facettes plates pour le verre taillé, lisses pour la sphère et la lentille
function makeGeometry(type) {
  let g;
  switch (type) {
    // (tous de rayon maximal ~ 1 : l'échelle de l'objet est son rayon projeté)
    case 'prism': g = new THREE.CylinderGeometry(0.8, 0.8, 1.3, 3, 1).toNonIndexed(); g.computeVertexNormals(); break;
    case 'cube': g = new THREE.BoxGeometry(1.15, 1.15, 1.15).toNonIndexed(); g.computeVertexNormals(); break;
    case 'crystal': g = new THREE.DodecahedronGeometry(1.0, 0).toNonIndexed(); g.computeVertexNormals(); break;
    case 'lens': g = new THREE.SphereGeometry(1.0, 48, 32); g.scale(1, 1, 0.3); g.computeVertexNormals(); break;
    default: g = new THREE.SphereGeometry(1.0, 48, 32);
  }
  return g;
}

// =============================================================================

export class GlassVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._L = TIER[this._tier] || TIER[1];
    const ps = parseInt(urlParam('glassset'), 10);
    this._forceSet = Number.isFinite(ps) && ps >= 0 ? ps % SETS.length : null;
    const pd = parseFloat(urlParam('glassdisp'));
    this._forceDisp = Number.isFinite(pd) ? pd : null;
    const bg = urlParam('glassbg');
    this._forceBg = BG_MODES.includes(bg) ? bg : null;
    this._showObjs = urlParam('glassobjs') !== '0';
    this._post = new VdjPost({ bloomStrength: 0.25, bloomThreshold: 0.85, grain: 0.01, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._postValue = this._post.update(1);
    this._comfort = new VdjComfort(VDJ_PRESETS.lounge.comfort);
    if (urlParam('glassprobe') === '1' && typeof window !== 'undefined') window.__glass = this;
  }

  get postProcessing() { return this._postValue; }

  init(scene) {
    this._sceneRef = scene;
    this._size = new THREE.Vector2(1280, 720);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    // le fond
    this._bgRT = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false });
    this._BU = { uCover: { value: null }, uHasCover: { value: 0 }, uAspect: { value: 1.78 }, uMode: { value: 0 }, uDim: { value: BG_DIM }, uTime: { value: 0 },
      uFrame: { value: new THREE.Vector4(1, 1, 0.5, 0.5) }, uCenter: { value: new THREE.Vector2() },
      uColA: { value: new THREE.Vector3(0.9, 0.3, 0.6) }, uColB: { value: new THREE.Vector3(0.3, 0.6, 1) }, uColC: { value: new THREE.Vector3(0.9, 0.8, 0.3) },
      uCaustic: { value: Array.from({ length: 5 }, () => new THREE.Vector4()) }, uKaleido: { value: 0 } };
    this._bgScene = new THREE.Scene();
    const bq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._BU, vertexShader: QUAD_VERTEX, fragmentShader: BG_FRAGMENT, depthTest: false, depthWrite: false }));
    bq.frustumCulled = false; this._bgScene.add(bq);
    // la scène du verre : le fond en quad, les faces arrière (cible), les faces avant
    this._cam = new THREE.PerspectiveCamera(2 * Math.atan(1 / CAM_D) * 180 / Math.PI, 16 / 9, 0.1, 20);
    this._cam.position.set(0, 0, CAM_D); this._cam.lookAt(0, 0, 0);
    this._backRT = new THREE.WebGLRenderTarget(16, 16, { type: THREE.HalfFloatType, depthBuffer: true, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false });
    this._backMat = new THREE.ShaderMaterial({ vertexShader: BACK_VERTEX, fragmentShader: BACK_FRAGMENT, side: THREE.BackSide });
    this._GU = { tBg: { value: this._bgRT.texture }, tBack: { value: this._backRT.texture }, uRes: { value: this._size }, uProjView: { value: new THREE.Matrix4() }, uIor: { value: 1.5 }, uDisp: { value: 0.02 }, uBgZ: { value: BG_Z }, uSpark: { value: 0 }, uGain: { value: 1 }, uTime: { value: 0 },
      uColA: this._BU.uColA, uColB: this._BU.uColB };
    this._glassMat = new THREE.ShaderMaterial({ uniforms: this._GU, vertexShader: GLASS_VERTEX, fragmentShader: glassFragment(this._L.disp === 'triple'), side: THREE.FrontSide });
    this._geos = {}; for (const t of ['prism', 'sphere', 'cube', 'crystal', 'lens']) this._geos[t] = makeGeometry(t);
    this._frontScene = new THREE.Scene(); this._backScene = new THREE.Scene();
    this._bgDisp = { uTex: { value: this._bgRT.texture } };
    const fq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._bgDisp, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    fq.frustumCulled = false; fq.renderOrder = -10; this._frontScene.add(fq);
    this._front = []; this._back = [];
    for (let i = 0; i < 5; i++) {
      const f = new THREE.Mesh(this._geos.sphere, this._glassMat); f.frustumCulled = false; f.visible = false; this._frontScene.add(f); this._front.push(f);
      const b = new THREE.Mesh(this._geos.sphere, this._backMat); b.frustumCulled = false; b.visible = false; this._backScene.add(b); this._back.push(b);
    }
    // la cible MSAA privée, l'affichage, le confort
    this._target = null;
    this._dispU = { uTex: { value: null } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    this._display.onBeforeRender = (renderer) => this._render(renderer);
    scene.add(this._display);
    this._lumTarget = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(16 * 16 * 4);
    this._lumU = { uTex: { value: null } };
    this._lumScene = new THREE.Scene();
    const lq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumU, vertexShader: QUAD_VERTEX, fragmentShader: LUM_FRAGMENT, depthTest: false, depthWrite: false }));
    lq.frustumCulled = false; this._lumScene.add(lq);
    this._lumAge = 0; this._lumMeasured = 0.2; this._lumPending = false; this._lumAvg = 0.2; this._gain = 1; this._gainRendered = 1;

    // l'état
    this._adapt = new Adapt(); this._a = this._adapt.get();
    this._sceneClock = new SceneClock({ period: SCENE_S, start: 0 });
    this._time = 0; this._paused = false;
    this._setIdx = this._forceSet != null ? this._forceSet : 0; this._prevSetIdx = this._setIdx; this._setAt = -99;
    this._bgIdx = 0; this._low = 0.3; this._high = 0.3; this._kick = 0; this._kickVel = 0; this._spark = 0; this._sparkObj = 0; this._breath = 0;
    this._dropAt = null; this._align = 0; this._kaleido = 0; this._rot = [];
    for (let i = 0; i < 5; i++) this._rot.push({ x: i * 0.7, y: i * 1.1, z: 0, speed: 0.25 + 0.1 * (i % 3) });
    this._prevAngles = [];
    this._coverUrl = undefined; this._coverTex = null; this._coverAspect = 1; this._paletteSeen = undefined;
    this._stats = { beats: 0, bars: 0, phrases: 0, scenes: 0, drops: 0 };
    this._applyPalette(null);
    this._applySet();
  }

  _applyPalette(pal) {
    if (pal === this._paletteSeen && pal !== null) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bd8ff, 0xffd23f];
    const put = (u, hex) => { const c = vdjColor(hex, undefined, 0.85); u.set(c.r, c.g, c.b); };
    put(this._BU.uColA.value, v[0]); put(this._BU.uColB.value, v[1 % v.length]); put(this._BU.uColC.value, v[2 % v.length]);
  }
  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._BU.uHasCover.value = 0;
    if (!profile.artworkUrl) return;
    const url = profile.artworkUrl;
    loadArtworkTexture(url, { mipmaps: false, wrap: THREE.ClampToEdgeWrapping }, (tex) => {
      if (!this._sceneRef || url !== this._coverUrl) { tex.dispose(); return; }
      this._coverTex = tex; this._BU.uCover.value = tex; this._BU.uHasCover.value = 1;
      const img = tex.image; this._coverAspect = img && img.width ? img.width / img.height : 1;
    }, () => {});
  }
  // l'ensemble courant : les maillages des objets ; le fondu de taille se fait au placement
  _applySet() {
    const set = SETS[this._setIdx % SETS.length];
    for (let i = 0; i < 5; i++) {
      const o = set[i];
      const on = !!o && i < this._L.objs;
      this._front[i].visible = on && this._showObjs; this._back[i].visible = on;
      if (on) { this._front[i].geometry = this._geos[o.t]; this._back[i].geometry = this._geos[o.t]; }
    }
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState != null && profile.playbackState !== 'PLAYING';
    this._applyPalette(profile.artworkPalette);
    this._loadCover(profile);
    const dt = this._paused ? 0 : Math.min(Math.max(deltaTime, 0), 0.1);
    const a = this._a = this._adapt.update(dt, profile);
    this._sceneClock.update(dt);
    if (this._paused) return;                     // pause : gel
    this._time += dt;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 110, beatS = 60 / bpm;
    const party = a.intensity > 0.7;
    const bands = profile.director && profile.director.bands;
    const real = bands && (bands.low > 0 || bands.high > 0);
    this._low += ((real ? clamp01(bands.low) : 0.35 + 0.3 * Math.sin(this._time * 0.9)) - this._low) * (1 - Math.exp(-dt / 0.2));
    this._high += ((real ? clamp01(bands.high) : 0.35 + 0.25 * Math.sin(this._time * 1.7)) - this._high) * (1 - Math.exp(-dt / 0.15));

    // SCÈNE : l'ensemble suivant ; PHRASE : la variante du fond ; MESURE : l'à-coup ; TEMPS : respiration et éclat
    if (this._sceneClock.wrapped && this._forceSet == null) { this._prevSetIdx = this._setIdx; this._setIdx = (this._setIdx + 1) % SETS.length; this._setAt = this._time; this._stats.scenes++; this._applySet(); }
    if (a.phrase) { this._stats.phrases++; this._bgIdx = (this._bgIdx + 1) % BG_CYCLE; }
    if (a.bar) { this._stats.bars++; this._kickVel += party ? 2.2 : 0.9; }
    if (a.beat) { this._stats.beats++; this._breath = 1; this._spark = 1; this._sparkObj = (this._sparkObj + 1) % 5; }
    this._breath *= Math.exp(-dt / (party ? 0.18 : 0.4));
    this._spark *= Math.exp(-dt / 0.25);
    this._kickVel *= Math.exp(-dt / 0.35); this._kick += this._kickVel * dt;
    // LE DROP : alignement devant le centre et kaléidoscope pendant deux mesures
    if (a.drop && a.dropReady && this._dropAt == null) {
      this._dropAt = this._time; this._stats.drops++;
      if (party && profile.director && this._comfort.requestFlash(this._time)) profile.director.flash = VDJ_DROP_FLASH;
    }
    if (this._dropAt != null) {
      const d = (this._time - this._dropAt) / beatS;
      const w = d < 8 ? smooth(d / 1.0) * (1 - smooth((d - 7) / 1.0)) : 0;
      this._align = w; this._kaleido = w;
      if (d >= 8) this._dropAt = null;
    } else { this._align *= Math.exp(-dt / 0.3); this._kaleido = this._align; }

    // les objets : placement, rotation (lente, à-coup), taille (respiration, fondu de scène, basse)
    const set = SETS[this._setIdx % SETS.length], prevSet = SETS[this._prevSetIdx % SETS.length];
    const A = safeZoneUniforms.uAspect.value || 1.78;
    const fade = smooth((this._time - this._setAt) / 1.5);
    const breathAmp = party ? 0.12 : 0.06;
    const sc = safeZoneUniforms.uStageCenter.value;
    for (let i = 0; i < 5; i++) {
      const o = set[i], po = prevSet[i];
      const m = this._front[i], b = this._back[i];
      if (!o && !po) { m.visible = false; b.visible = false; continue; }
      const cur = o ? placeObject(o, i, this._time + this._setIdx * 7.3, A, this._low, this._align) : null;
      const prev = po ? placeObject(po, i, this._time + this._prevSetIdx * 7.3, A, this._low, this._align) : null;
      const P = cur || prev;
      let s = (cur ? cur.scale : 0) * fade + (prev ? prev.scale : 0) * (1 - fade);
      s *= 1 + breathAmp * this._breath * a.pulse;
      if (s < 0.01) { m.visible = false; b.visible = false; continue; }
      m.visible = this._showObjs && i < this._L.objs; b.visible = i < this._L.objs;
      m.position.set(sc.x + P.x, sc.y + P.y, P.z); b.position.copy(m.position);
      const r = this._rot[i];
      r.x += dt * r.speed * 0.6 * a.motion; r.y += dt * r.speed * a.motion + this._kickVel * dt * (i % 2 ? 1 : -1) * 0.5;
      m.rotation.set(r.x, r.y, r.z); b.rotation.copy(m.rotation);
      m.scale.setScalar(s); b.scale.setScalar(s);
      // la caustique : un éclat qui part de l'objet dans la direction de sa rotation, fort quand il tourne vite
      const omega = Math.abs(r.speed * a.motion + this._kickVel * 0.5);
      this._BU.uCaustic.value[i].set(sc.x + P.x, sc.y + P.y, r.y * 1.3 + i, m.visible ? clamp01((omega - 0.25) * 1.5) * 0.7 : 0);
    }
    // le fond (R18), la dispersion, les éclats
    const BU = this._BU;
    const fr = coverFrame(A, this._coverAspect, this._time);
    BU.uFrame.value.set(fr.visW, fr.visH, fr.cx, fr.cy);
    BU.uMode.value = BG_MODES.indexOf(this._forceBg || BG_MODES[this._bgIdx]);
    BU.uTime.value = this._time; BU.uAspect.value = A; BU.uCenter.value.set(sc.x, sc.y); BU.uKaleido.value = this._kaleido;
    const GU = this._GU;
    GU.uDisp.value = this._forceDisp != null ? this._forceDisp : 0.012 + 0.05 * this._high;
    GU.uSpark.value = 0;                           // (posé par objet au rendu : un seul objet brille)
    GU.uTime.value = this._time;
    // R10 (lounge : 0,35 ; party : 0,45), mesuré sur la cible privée
    this._lumAge += dt;
    if (this._lumAge >= 0.5) { this._lumAge = 0; this._lumPending = true; }
    this._lumAvg += (this._lumMeasured - this._lumAvg) * Math.min(1, dt / 1.0);
    const cap = 0.9 * (party ? 0.45 : 0.35);
    const gt = Math.min(1, cap / Math.max(1e-3, this._lumAvg));
    this._gain += (gt - this._gain) * Math.min(1, dt / 0.5);
    GU.uGain.value = this._gain; BU.uDim.value = BG_DIM * this._gain;
    this._postValue = this._post.update(party ? vdjBeatChroma(a.beatPhase, beatS) : 1);
    this._beatS = beatS; this._party = party;
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    if (prev) this._size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(this._size);
    const w = Math.max(16, Math.round(this._size.x)), h = Math.max(16, Math.round(this._size.y));
    if (!this._target || this._target.width !== w || this._target.height !== h) {
      if (this._target) this._target.dispose();
      this._target = new THREE.WebGLRenderTarget(w, h, { samples: this._L.msaa, depthBuffer: true, stencilBuffer: false, type: THREE.UnsignedByteType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
      this._dispU.uTex.value = this._target.texture; this._lumU.uTex.value = this._target.texture;
      this._bgRT.setSize(w, h);
      const bw = Math.max(16, Math.round(w * this._L.backScale)), bh = Math.max(16, Math.round(h * this._L.backScale));
      this._backRT.setSize(bw, bh);
    }
    if (this._paused && this._rendered) return;
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    this._cam.aspect = w / h; this._cam.updateProjectionMatrix(); this._cam.updateMatrixWorld();
    this._GU.uProjView.value.multiplyMatrices(this._cam.projectionMatrix, this._cam.matrixWorldInverse);
    // 1. le fond
    renderer.setRenderTarget(this._bgRT); renderer.render(this._bgScene, this._ortho);
    // 2. les faces arrière (normale, profondeur)
    renderer.setRenderTarget(this._backRT); renderer.setClearColor(0x000000, 0); renderer.clear(true, true, false);
    renderer.render(this._backScene, this._cam);
    // 3. le fond puis les faces avant, dans la cible MSAA ; l'éclat du temps sur un seul objet
    this._gainRendered = this._gain;
    renderer.setRenderTarget(this._target); renderer.setClearColor(0x000000, 1); renderer.clear(true, true, false);
    for (let i = 0; i < 5; i++) {
      const m = this._front[i];
      m.onBeforeRender = (i === this._sparkObj) ? () => { this._GU.uSpark.value = this._spark; } : () => { this._GU.uSpark.value = 0; };
    }
    renderer.render(this._frontScene, this._cam);
    this._rendered = true;
    if (this._lumPending) {
      this._lumPending = false;
      renderer.setRenderTarget(this._lumTarget);
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 16, 16, this._lumPixels);
      let sum = 0; for (let i = 0; i < this._lumPixels.length; i += 4) sum += this._lumPixels[i];
      this._lumMeasured = sum / (256 * 255) / Math.max(0.05, this._gainRendered);
    }
    renderer.setRenderTarget(prev);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
  }

  getProbe() {
    const objs = [];
    for (let i = 0; i < 5; i++) { const m = this._front[i]; if (m.visible) objs.push({ t: Object.keys(this._geos).find((k) => this._geos[k] === m.geometry), x: +m.position.x.toFixed(3), y: +m.position.y.toFixed(3), z: +m.position.z.toFixed(3), s: +m.scale.x.toFixed(3) }); }
    return { tier: this._tier, set: this._setIdx, bg: BG_MODES[this._BU.uMode.value], objs, disp: +this._GU.uDisp.value.toFixed(4), kaleido: +this._kaleido.toFixed(2), align: +this._align.toFixed(2), party: !!this._party,
      intensity: +this._a.intensity.toFixed(3), lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3), hasCover: this._BU.uHasCover.value, frame: this._BU.uFrame.value.toArray().map((x) => +x.toFixed(4)), stats: Object.assign({}, this._stats) };
  }

  dispose() {
    if (this._sceneRef && this._display) { this._display.onBeforeRender = null; this._sceneRef.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    for (const rt of [this._target, this._bgRT, this._backRT, this._lumTarget]) if (rt) rt.dispose();
    for (const k in this._geos) this._geos[k].dispose();
    if (this._glassMat) this._glassMat.dispose(); if (this._backMat) this._backMat.dispose();
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__glass === this) window.__glass = null;
    this._sceneRef = null;
  }
}
